import {Button, Dialog, Tab, TabId, Tabs} from "@blueprintjs/core";
import log from "loglevel";
import styled from "@emotion/styled";
import {forwardRef, useContext, useEffect, useImperativeHandle, useState} from "react";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";
import {useSetUserPrefMutation} from "../../../../generated/graphql";
import {
  PreferenceContext,
  createJsonPref,
  extractJsonPref,
  extractSimplePref
} from "../../../../providers/PreferenceProvider";
import {TenantPreferences, UserPreferences, COLORS} from "../../../../constants/constants";
import PresentContinuouslyForm from "./PresentContinuouslyForm";
import PresentUponHoverForm from "../common/components/PresentUponHoverForm";
import CardTypeSelector from "./components/CardTypeSelector";
import {getDefaultCardConfigurations, HOVERS_ON_DRIVERS_OPTIONS} from "./constants/constants";
import {IManifestCardConfigurationsV2} from "./types/manifest-card-configurations.type";
import {useFeatureFlagContext} from "../../../../providers/FeatureFlagProvider";
import {DriverInformationValue} from "./constants/enums";
import {AppToaster} from "../../../../utils/toasters/toaster";

const {
  DARK_BLUE: COLOR_PRIMARY,
  TEXT_BLACK: COLOR_TEXT,
  BG_GRAY: COLOR_BG,
  BG_DISABLED: COLOR_DISABLED,
  WHITE: COLOR_WHITE
} = COLORS;

export type ConfigurationDialogRef = {
  onOpenDialog: () => void;
};

// eslint-disable-next-line react/display-name
const ConfigurationDialog = forwardRef<ConfigurationDialogRef>((_, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState<TabId>(0);
  const [setUserPref, {loading}] = useSetUserPrefMutation();
  const {userPreferences, tenantPreferences, userPrefsQueryRefetch} = useContext(PreferenceContext);
  const {skills: isSkillsEnabled} = useFeatureFlagContext();
  const isUseETAService = extractSimplePref(tenantPreferences, TenantPreferences.useEtaService, true).value;

  const onOpenDialog = () => {
    setIsOpen(true);
  };

  const onCloseDialog = () => {
    reset();
    setIsOpen(false);
    setCurrentTab(0);
  };

  useImperativeHandle(ref, () => ({
    onOpenDialog
  }));

  const initialValue: IManifestCardConfigurationsV2 = extractJsonPref(
    userPreferences,
    UserPreferences.manifestCardConfigurations,
    getDefaultCardConfigurations(isUseETAService)
  ).value;

  initialValue.hoverDriverConfig = HOVERS_ON_DRIVERS_OPTIONS.filter((option) =>
    initialValue.hoverDriverConfig.map((item) => item.value).includes(option.value)
  );

  const methods = useForm<IManifestCardConfigurationsV2>({defaultValues: initialValue});

  const {
    formState: {isDirty},
    handleSubmit,
    reset,
    getValues
  } = methods;

  const onSubmit: SubmitHandler<IManifestCardConfigurationsV2> = async (data) => {
    try {
      const updatedValue = createJsonPref(data, true);
      await setUserPref({
        variables: {
          name: UserPreferences.manifestCardConfigurations,
          input: updatedValue
        }
      });
      userPrefsQueryRefetch?.();
      reset(data);
      setCurrentTab(0);
      onCloseDialog();
      AppToaster.show({
        message:
          "You've updated the data on your cards. Glad we can provide more relevant data. This change will take effect after a brief period of bringing in these data points.",
        intent: "success"
      }); // Show success message
    } catch (error) {
      log.error(error);
    }
  };

  useEffect(() => {
    const currentDriverInfo = getValues("driverInformation") ?? [];
    if (!isSkillsEnabled && currentDriverInfo.includes(DriverInformationValue.DriverSkills)) {
      const updatedData = {
        ...getValues(),
        driverInformation: currentDriverInfo.filter((el) => el !== DriverInformationValue.DriverSkills)
      };
      setUserPref({
        variables: {
          name: UserPreferences.manifestCardConfigurations,
          input: createJsonPref(updatedData, true)
        }
      }).then(() => {
        userPrefsQueryRefetch?.();
        reset(updatedData);
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSkillsEnabled]);

  return (
    <StyledDialog data-testid="" title="manifest card content settings" isOpen={isOpen} onClose={onCloseDialog}>
      <FormProvider {...methods}>
        <StyledForm
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit(onSubmit)}
        >
          <DialogBody style={{pointerEvents: loading ? "none" : "auto"}}>
            <Section>
              <SectionTitle>Card Type</SectionTitle>
              <CardTypeSelector />
            </Section>
            <Section style={{overflow: "hidden"}}>
              <SectionTitle>Card Content</SectionTitle>
              <Tabs onChange={setCurrentTab} selectedTabId={currentTab} size="large">
                <Tab
                  id={0}
                  title="Present Continuously"
                  panel={<PresentContinuouslyForm />}
                  style={{fontWeight: currentTab === 0 ? "500" : "normal"}}
                />
                <Tab
                  id={1}
                  title="Present Upon Hover"
                  panel={<PresentUponHoverForm />}
                  style={{fontWeight: currentTab === 1 ? "500" : "normal"}}
                />
              </Tabs>
            </Section>
          </DialogBody>
          <DialogFooter>
            <ActionsContainer>
              <CancelButton data-testid="cancel-button" type="button" onClick={onCloseDialog}>
                Cancel
              </CancelButton>
              <UpdateButton data-testid="update-button" disabled={!isDirty} type="submit" loading={loading}>
                Update
              </UpdateButton>
            </ActionsContainer>
          </DialogFooter>
        </StyledForm>
      </FormProvider>
    </StyledDialog>
  );
});

export default ConfigurationDialog;

const StyledDialog = styled(Dialog)`
  margin: 0;
  height: 90vh;
  width: 920px;
  font-family: "Roboto", sans-serif;
  color: ${COLOR_TEXT};
  display: flex;
  flex-direction: column;
  position: relative;

  .bp5-dialog-header {
    min-height: 48px;
    height: 48px;
    padding: 16px 24px;
    background-color: ${COLOR_BG};

    .bp5-heading {
      font-weight: 500;
      font-size: 14px;
      text-transform: uppercase;
    }
  }

  .bp5-tabs {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-grow: 1;

    .bp5-tab-list {
      color: ${COLOR_TEXT};
      height: 32px;

      .bp5-tab-indicator-wrapper {
        z-index: 2;
        .bp5-tab-indicator {
          height: 4px;
          background-color: ${COLOR_PRIMARY};
          border-radius: 2px;
        }
      }

      .bp5-tab {
        flex: 1;
        display: flex;
        justify-content: center;
      }

      ::after {
        content: "";
        z-index: 1;
        position: absolute;
        width: 100%;
        bottom: 1px;
        border-bottom: 2px solid ${COLOR_DISABLED};
      }
    }

    .bp5-tab-panel {
      flex-grow: 1;
      overflow-y: auto;
      flex-direction: column;
    }
  }
`;

const StyledForm = styled.form`
  overflow: hidden;
  flex-grow: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
  background-color: ${COLOR_WHITE};
  padding: 16px 24px;
  gap: 24px;
`;

const DialogFooter = styled.div`
  background-color: ${COLOR_BG};
  border-radius: 0px 0px 4px 4px;
  padding: 16px 24px;
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-weight: 500;
  font-size: 16px;
  color: ${COLOR_TEXT};
`;

const UpdateButton = styled(Button)`
  &.bp5-button:not([class*="bp5-intent-"]) {
    color: ${COLOR_WHITE};
    background-color: ${COLOR_PRIMARY};
    background: linear-gradient(0deg, rgba(15, 18, 23, 0.1), rgba(15, 18, 23, 0.1)),
      linear-gradient(0deg, ${COLOR_PRIMARY}, ${COLOR_PRIMARY}) !important;
    border: 1px solid ${COLOR_DISABLED} !important;
    box-shadow: 0px 1px 2px 0px #0000000d !important;
  }

  &.bp5-button:not([class*="bp5-intent-"]):disabled {
    color: #fbfbfb;
    background-color: ${COLOR_DISABLED};
    background: linear-gradient(0deg, rgba(15, 18, 23, 0.1), rgba(15, 18, 23, 0.1)),
      linear-gradient(0deg, ${COLOR_DISABLED}, ${COLOR_DISABLED}) !important;
    border: 1px solid ${COLOR_DISABLED} !important;
    box-shadow: 0px 1px 2px 0px #0000000d !important;
  }
  font-size: 16px;
  font-weight: 400;
  border-radius: 4px;
  height: 32px;
  padding: 6px 14px;
  border: 1px;
  gap: 7px;
`;

const CancelButton = styled.button`
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: ${COLOR_PRIMARY};
  font-size: 16px;
  font-weight: 500;
  height: 32px;
  border: none !important;

  :disabled {
    color: ${COLOR_DISABLED};
    cursor: not-allowed;
  }
`;
